import { stringify } from 'qs';
import type { ReactNode, SyntheticEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';

import type { OnBackForgotPassword } from '@sorare/wallet-shared';

import { TermsAndConditionsStatus } from '#src/__generated__/globalTypes.ts';
import type { GoogleReCAPTCHA } from '#src/components/recaptcha/index.tsx';
import { ReCAPTCHA } from '#src/components/recaptcha/index.tsx';
import { TERMS_AND_CONDITIONS } from '#src/constants/__generated__/routes.ts';
import {
  OAUTH_LOCKED_ACCOUNT_ERROR_CODE,
  OAUTH_MISSING_EMAIL_ERROR_CODE,
} from '#src/constants/errors.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import {
  Level,
  useSnackNotificationContext,
} from '#src/contexts/snackNotification/index.ts';
import {
  useMessagingContext,
  useWalletContext,
} from '#src/contexts/wallet/index.ts';
import { useSkipOneDayEmailVerificationLatency } from '#src/hooks/useSkipOneDayEmailVerificationLatency.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';

import {
  ConnectionDialog,
  isConnectionDialogMode,
} from './ConnectionDialog/index.tsx';
import { NewDeviceDialog } from './NewDeviceDialog/index.tsx';
import { PasswordForgottenDialog } from './PasswordForgottenDialog/index.tsx';
import { PromptTermsDialog } from './PromptTermsDialog/index.tsx';
import { PromptTwoFactAuthDialog } from './PromptTwoFactAuthDialog/index.tsx';
import { ResetPasswordDialog } from './ResetPasswordDialog/index.tsx';
import { ConnectionContext } from './index.tsx';
import type {
  AcceptTermsInfo,
  PopMode,
  Prompt2faCallback,
  PromptTermsCallback,
  PromptTermsOptions,
  SignUpConfig,
} from './index.tsx';
import { useConnectionMode } from './useConnectionMode.ts';

type Props = {
  children: ReactNode;
};

const INITIAL_TERMS_STATUS = TermsAndConditionsStatus.INITIAL.toLowerCase();

export const ConnectionProvider = ({ children }: Props) => {
  const [signupConfig, setSignupConfig] = useState<SignUpConfig | undefined>(
    null
  );
  const [searchParams] = useSearchParams();

  const code = searchParams.get('code');
  const id = searchParams.get('id') || undefined;
  const tcuToken = searchParams.get('tcuToken');
  const termStatus = searchParams.get('termsStatus');
  const challengeFromQuery = searchParams.get('otp_session_challenge');
  const [popMode, setPopMode] = useState<PopMode | null>(null);
  const [isMobileWebviewSignUp, setIsMobileWebviewSignUp] =
    useState<boolean>(false);
  const [otpSessionChallenge, setOtpSessionChallenge] =
    useState(challengeFromQuery);
  const skipOneDayEmailVerificationLatency =
    useSkipOneDayEmailVerificationLatency();
  const { showNotification } = useSnackNotificationContext();

  const { currentUser } = useCurrentUserContext();
  const [passwordForgotten, setPasswordForgotten] = useState(false);
  const [prompt2faCallback, setPrompt2faCallback] =
    useState<Prompt2faCallback | null>(null);
  const [promptTermsCallback, setPromptTermsCallback] =
    useState<PromptTermsCallback | null>(null);
  const [reasonFor2Fa, setReasonFor2FA] = useState<string | undefined>();
  const [promptTermsOptions, setPromptTermsOptions] =
    useState<PromptTermsOptions>({});
  const [acceptedTerms, setAcceptedTerms] = useState<AcceptTermsInfo | null>(
    null
  );
  const { passwordForgotten: promptPasswordForgotten } = useWalletContext();
  const recaptchaRef = useRef<GoogleReCAPTCHA>(null);
  // store the initial value of mustAcceptTcus to
  // avoid the redirection to `TERMS` once accepted
  const [initialMustAcceptTCU] = useState(!!currentUser?.mustAcceptTcus);
  const [initialTCUAcceptance] = useState(termStatus === INITIAL_TERMS_STATUS);
  const [mode, setMode] = useConnectionMode({
    mustAcceptTCU: initialMustAcceptTCU,
  });
  const shouldRedirectToTerms = mode === 'showTerms' && !initialMustAcceptTCU;

  const { registerHandler } = useMessagingContext();
  const track = useEvents();

  const signIn = useCallback(() => {
    track('Click Sign In');
    setAcceptedTerms(null);
    setSignupConfig(undefined);
    return setMode('signin');
  }, [track, setMode]);

  const signUp = useCallback(
    (e?: SyntheticEvent, config?: SignUpConfig) => {
      track('Click Sign Up', {
        variation: '',
        forceEmailConfirmation: skipOneDayEmailVerificationLatency,
      });
      setSignupConfig(config);
      setAcceptedTerms(null);
      return setMode('signup');
    },
    [track, skipOneDayEmailVerificationLatency, setMode]
  );

  const showMobileWebviewSignUpFlow = useCallback(() => {
    setAcceptedTerms(null);
    setIsMobileWebviewSignUp(true);
    setMode('signup');
  }, [setMode]);
  const showMobilePasswordForgotten = useCallback(() => {
    setAcceptedTerms(null);
    setIsMobileWebviewSignUp(true);
    setMode('forgotPassword');
  }, [setMode]);
  const toggleResetPassword = useCallback(() => {
    setMode('signin');
  }, [setMode]);

  const handleClose = useCallback(() => setMode(null), [setMode]);
  const handleTermsClose = useCallback(() => setPopMode(null), [setPopMode]);

  const closeConnectionDialog = useCallback(() => {
    setMode(null);
    setPopMode(null);
  }, [setMode]);

  const prompt2fa = useCallback(
    (cb: Prompt2faCallback, challenge: string, reason?: string) => {
      setMode('2fa');
      setPrompt2faCallback(cb);
      setOtpSessionChallenge(challenge);
      setReasonFor2FA(reason);
    },
    [setOtpSessionChallenge, setMode]
  );

  const promptTerms = useCallback(
    (options: PromptTermsOptions, cb?: PromptTermsCallback) => {
      setPopMode('mustAcceptTerms');
      setPromptTermsOptions(options);
      if (cb) setPromptTermsCallback(cb);
    },
    []
  );

  const promptNewDeviceConfirmation = useCallback(
    () => setMode('newDevice'),
    [setMode]
  );

  useEffect(() => {
    if (passwordForgotten) promptPasswordForgotten();
    registerHandler<OnBackForgotPassword>('onBackForgotPassword', async () => {
      setPasswordForgotten(false);
      return {};
    });
  }, [passwordForgotten, promptPasswordForgotten, registerHandler]);

  useEffect(() => {
    if (mode === 'signup') {
      if (code === OAUTH_MISSING_EMAIL_ERROR_CODE) {
        showNotification('oauthMissingEmail', {}, { level: Level.ERROR });
      } else if (code === OAUTH_LOCKED_ACCOUNT_ERROR_CODE) {
        showNotification('oauthLockedAccount', {}, { level: Level.ERROR });
      }
    }
  }, [code, mode, showNotification]);

  const value = useMemo(
    () => ({
      signIn,
      signUp,
      showMobileWebviewSignUpFlow,
      showMobilePasswordForgotten,
      setPasswordForgotten,
      toggleResetPassword,
      prompt2fa,
      promptTerms,
      passwordForgotten,
      promptNewDeviceConfirmation,
      acceptedTerms,
      closeConnectionDialog,
      recaptchaRef,
    }),
    [
      signIn,
      signUp,
      showMobileWebviewSignUpFlow,
      showMobilePasswordForgotten,
      setPasswordForgotten,
      toggleResetPassword,
      prompt2fa,
      promptTerms,
      passwordForgotten,
      promptNewDeviceConfirmation,
      acceptedTerms,
      closeConnectionDialog,
    ]
  );

  if (mode === 'forgotPassword' && !passwordForgotten && !currentUser) {
    setPasswordForgotten(true);
  }

  if (
    mode &&
    [
      'recoverKey',
      'restoreWallet',
      'addFundsEth',
      'addFunds',
      'kyc',
      'verifyPhone',
    ].includes(mode) &&
    !currentUser
  ) {
    const afterLoggedInTarget = `?${stringify({ action: mode, id })}`;
    return (
      <Navigate
        to="?action=signin"
        state={{
          afterLoggedInTarget,
        }}
        replace
      />
    );
  }

  if (shouldRedirectToTerms) {
    return <Navigate to={TERMS_AND_CONDITIONS} replace />;
  }

  return (
    <ConnectionContext value={value}>
      {isConnectionDialogMode(mode) && !passwordForgotten && !currentUser && (
        <ConnectionDialog
          open
          isMobileWebviewSignUp={isMobileWebviewSignUp}
          mode={mode}
          popMode={popMode}
          setMode={setMode}
          onClose={handleClose}
          signupConfig={signupConfig}
        />
      )}
      {!!passwordForgotten && (
        <PasswordForgottenDialog
          open
          onBack={() => setPasswordForgotten(false)}
        />
      )}
      {mode === 'resetPassword' && (
        <ResetPasswordDialog open onClose={handleClose} />
      )}
      {mode === 'newDevice' && <NewDeviceDialog onClose={handleClose} />}
      {mode === '2fa' && !!otpSessionChallenge && (
        <PromptTwoFactAuthDialog
          open
          otpSessionChallenge={otpSessionChallenge}
          onSignedIn={prompt2faCallback}
          onClose={handleClose}
          reason={reasonFor2Fa}
        />
      )}
      {/* OAUTH GRACE PERIOD */}
      {mode === 'showTerms' && !!initialMustAcceptTCU && (
        <PromptTermsDialog
          open
          onClose={handleClose}
          options={{
            closable: true,
            initialTermsDisplay: initialTCUAcceptance,
          }}
          onAccept={null}
          isMobileWebviewSignUp={isMobileWebviewSignUp}
        />
      )}

      {/* OAUTH AFTER GRACE PERIOD */}
      {mode === 'acceptTerms' && !!tcuToken && (
        <PromptTermsDialog
          open
          onClose={handleClose}
          options={{
            closable: false,
            tcuToken,
            initialTermsDisplay: initialTCUAcceptance,
          }}
          onAccept={promptTermsCallback}
          isMobileWebviewSignUp={isMobileWebviewSignUp}
        />
      )}
      {/* ALL THE REST  */}
      {popMode === 'mustAcceptTerms' && (
        <PromptTermsDialog
          open
          onAccept={promptTermsCallback}
          onClose={handleTermsClose}
          options={promptTermsOptions}
          isMobileWebviewSignUp={isMobileWebviewSignUp}
        />
      )}
      {!!(mode === 'signup' || mode === 'signin' || passwordForgotten) && (
        <ReCAPTCHA ref={recaptchaRef} />
      )}
      {children}
    </ConnectionContext>
  );
};
